import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { Employee, TimeEntry, Job } from '../../types/backend.types';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { Search, Calendar, Clock, Edit, Trash2, Plus, ChevronLeft, ChevronRight, X, Save, Users, ChevronDown } from 'lucide-react';
import { CustomTimePicker } from '../common/CustomTimePicker';
import { resolveClockOut, nextDayConfirmMessage } from '../../utils/easternTime';

interface TimeEntryWithDetails extends TimeEntry {
  job_name?: string;
  employee_name?: string;
  hours_worked?: number;
}

interface DayTotal {
  date: string;
  entries: TimeEntryWithDetails[];
  totalHours: number;
}

interface CrewMember {
  employee: Employee;
  entries: TimeEntryWithDetails[];
  clockInTime?: string;
  clockOutTime?: string;
  lunchInfo?: { taken: boolean; minutes: number };
  totalHours: number;
}

export const TimeManagement: React.FC = () => {
  const auth = useContext(AuthContext);

  // Admin-only access control
  if (!auth?.user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  if (auth.user.role !== 'admin') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is only accessible to administrators.</p>
        <p>Your current role: <strong>{auth.user.role}</strong></p>
      </div>
    );
  }

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [timeEntries, setTimeEntries] = useState<TimeEntryWithDetails[]>([]);
  const [weekDays, setWeekDays] = useState<DayTotal[]>([]);
  const [totalWeekHours, setTotalWeekHours] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Crew view state
  const [viewMode, setViewMode] = useState<'individual' | 'crew'>('individual');
  const [crewData, setCrewData] = useState<{ [date: string]: CrewMember[] }>({});
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<TimeEntryWithDetails | null>(null);
  const [editingEntry, setEditingEntry] = useState<TimeEntryWithDetails | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    clockIn: '',
    clockOut: '',
    jobId: '',
    lunchTaken: false,
    lunchMinutes: 30
  });

  // Get week date range
  const getWeekRange = (offset: number = 0) => {
    const today = new Date();
    const currentDay = today.getDay();

    let daysSinceLastFriday;
    if (currentDay === 0) {
      daysSinceLastFriday = 2;
    } else if (currentDay >= 1 && currentDay <= 4) {
      daysSinceLastFriday = currentDay + 2;
    } else {
      daysSinceLastFriday = currentDay - 5;
    }

    const friday = new Date(today);
    friday.setDate(today.getDate() - daysSinceLastFriday + (offset * 7));
    friday.setHours(0, 0, 0, 0);

    const thursday = new Date(friday);
    thursday.setDate(friday.getDate() + 6);
    thursday.setHours(23, 59, 59, 999);

    return { friday, thursday };
  };

  const formatWeekRange = (offset: number = 0) => {
    const { friday, thursday } = getWeekRange(offset);
    const fridayStr = friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const thursdayStr = thursday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fridayStr} - ${thursdayStr}`;
  };

  // Load employees
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await api.get(API_CONFIG.ENDPOINTS.EMPLOYEES);
        if (response.data.success) {
          setEmployees(response.data.data);
        }
      } catch (err) {
        console.error('Failed to load employees:', err);
        setError('Failed to load employees');
      }
    };

    loadEmployees();
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const response = await api.get(API_CONFIG.ENDPOINTS.JOBS);
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  };

  useEffect(() => {
    if (selectedEmployee) {
      loadTimeEntries();
    }
  }, [selectedEmployee, weekOffset]);

  const loadTimeEntries = async () => {
    if (!selectedEmployee) return;

    setLoading(true);
    setError(null);

    try {
      const { friday, thursday } = getWeekRange(weekOffset);

      const response = await api.get(
        `${API_CONFIG.ENDPOINTS.TIME_ENTRIES}?employee_id=${selectedEmployee.employee_id}&start_date=${friday.toISOString()}&end_date=${thursday.toISOString()}`
      );

      if (response.data.success) {
        const entries = response.data.data;
        setTimeEntries(entries);
        processWeekData(entries);
      }
    } catch (err) {
      console.error('Failed to load time entries:', err);
      setError('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get local date string (YYYY-MM-DD) from UTC datetime - matches Admin Portal pattern
  const getLocalDateStr = (dateInput: Date | string) => {
    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const processWeekData = (entries: TimeEntryWithDetails[]) => {
    const { friday } = getWeekRange(weekOffset);
    const days: DayTotal[] = [];
    let weekTotal = 0;

    for (let i = 0; i < 7; i++) {
      const date = new Date(friday);
      date.setDate(friday.getDate() + i);
      const dateStr = getLocalDateStr(date);

      const dayEntries = entries
        .filter(entry => getLocalDateStr(entry.clock_in) === dateStr)
        .sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime());

      const entriesWithHours = dayEntries.map(entry => {
        let hours = 0;
        if (entry.clock_out) {
          const clockIn = new Date(entry.clock_in);
          const clockOut = new Date(entry.clock_out);
          hours = (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60);

          if (entry.lunch_taken && entry.lunch_minutes) {
            hours -= entry.lunch_minutes / 60;
          }
        }
        return { ...entry, hours_worked: Math.max(0, hours) };
      });

      const dayTotal = entriesWithHours.reduce((sum, entry) => sum + (entry.hours_worked || 0), 0);
      weekTotal += dayTotal;

      days.push({
        date: dateStr,
        entries: entriesWithHours,
        totalHours: dayTotal
      });
    }

    setWeekDays(days);
    setTotalWeekHours(weekTotal);
  };

  const loadCrewData = async () => {
    if (!selectedEmployee || selectedEmployee.role !== 'foreman') return;

    setLoading(true);
    setError(null);

    try {
      const { friday, thursday } = getWeekRange(weekOffset);
      const crewMembers = employees.filter(emp => emp.crew_foreman_id === selectedEmployee.employee_id);
      const allCrewIds = [selectedEmployee.employee_id, ...crewMembers.map(emp => emp.employee_id)];
      const allEntries: TimeEntryWithDetails[] = [];

      for (const empId of allCrewIds) {
        const response = await api.get(
          `${API_CONFIG.ENDPOINTS.TIME_ENTRIES}?employee_id=${empId}&start_date=${friday.toISOString()}&end_date=${thursday.toISOString()}`
        );

        if (response.data.success) {
          allEntries.push(...response.data.data);
        }
      }

      processCrewData(allEntries);
    } catch (err) {
      console.error('Failed to load crew data:', err);
      setError('Failed to load crew data');
    } finally {
      setLoading(false);
    }
  };

  const processCrewData = (entries: TimeEntryWithDetails[]) => {
    const { friday } = getWeekRange(weekOffset);
    const crewByDay: { [date: string]: CrewMember[] } = {};

    for (let i = 0; i < 7; i++) {
      const date = new Date(friday);
      date.setDate(friday.getDate() + i);
      const dateStr = getLocalDateStr(date);

      const dayEntries = entries.filter(entry => getLocalDateStr(entry.clock_in) === dateStr);
      const employeeMap = new Map<number, TimeEntryWithDetails[]>();

      dayEntries.forEach(entry => {
        const empId = entry.employee_id;
        if (!employeeMap.has(empId)) {
          employeeMap.set(empId, []);
        }
        employeeMap.get(empId)!.push(entry);
      });

      const dayCrewMembers: CrewMember[] = [];
      employeeMap.forEach((empEntries, empId) => {
        const employee = employees.find(emp => emp.employee_id === empId);
        if (!employee) return;

        const sortedEntries = empEntries.sort((a, b) =>
          new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime()
        );

        const firstEntry = sortedEntries[0];
        const lastEntry = sortedEntries[sortedEntries.length - 1];

        const clockInTime = new Date(firstEntry.clock_in).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        });

        const clockOutTime = lastEntry.clock_out ?
          new Date(lastEntry.clock_out).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Still clocked in';

        let totalHours = 0;
        let lunchTaken = false;
        let lunchMinutes = 0;

        empEntries.forEach(entry => {
          if (entry.clock_out) {
            const clockIn = new Date(entry.clock_in);
            const clockOut = new Date(entry.clock_out);
            let hours = (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60);

            if (entry.lunch_taken && entry.lunch_minutes) {
              hours -= entry.lunch_minutes / 60;
              lunchTaken = true;
              lunchMinutes = entry.lunch_minutes;
            }
            totalHours += Math.max(0, hours);
          }
        });

        dayCrewMembers.push({
          employee,
          entries: empEntries,
          clockInTime,
          clockOutTime,
          lunchInfo: { taken: lunchTaken, minutes: lunchMinutes },
          totalHours
        });
      });

      dayCrewMembers.sort((a, b) => {
        const timeA = a.entries[0] ? new Date(a.entries[0].clock_in).getTime() : 0;
        const timeB = b.entries[0] ? new Date(b.entries[0].clock_in).getTime() : 0;
        return timeA - timeB;
      });

      crewByDay[dateStr] = dayCrewMembers;
    }

    setCrewData(crewByDay);
  };

  useEffect(() => {
    if (selectedEmployee && selectedEmployee.role === 'foreman' && viewMode === 'crew') {
      loadCrewData();
    }
  }, [selectedEmployee, weekOffset, viewMode, employees]);

  const getTimeDifferenceStatus = (crewMembers: CrewMember[], member: CrewMember) => {
    if (crewMembers.length < 2) return 'normal';

    const firstMember = crewMembers[0];
    const firstClockIn = firstMember.entries[0] ? new Date(firstMember.entries[0].clock_in) : null;
    const memberClockIn = member.entries[0] ? new Date(member.entries[0].clock_in) : null;

    if (!firstClockIn || !memberClockIn) return 'normal';

    const diffMinutes = Math.abs(memberClockIn.getTime() - firstClockIn.getTime()) / (1000 * 60);
    return diffMinutes > 20 ? 'highlight' : 'normal';
  };

  const filteredEmployees = viewMode === 'individual'
    ? employees.filter(emp =>
        emp.is_active !== false &&
        (emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : employees
        .filter(emp => emp.is_active !== false && emp.role === 'foreman')
        .filter(emp =>
          emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.last_name.toLowerCase().includes(searchTerm.toLowerCase())
        );

  const handleEditEntry = (entry: TimeEntryWithDetails) => {
    setEditingEntry(entry);

    const clockInDate = new Date(entry.clock_in);
    const clockOutDate = entry.clock_out ? new Date(entry.clock_out) : null;

    setFormData({
      date: getLocalDateStr(clockInDate),  // Use local date to match local time
      clockIn: clockInDate.toTimeString().slice(0, 5),
      clockOut: clockOutDate ? clockOutDate.toTimeString().slice(0, 5) : '',
      jobId: entry.job_id?.toString() || '',
      lunchTaken: entry.lunch_taken || false,
      lunchMinutes: entry.lunch_minutes || 30
    });

    // Set job search term to the job name for display
    const selectedJob = jobs.find(j => j.job_id === entry.job_id);
    if (selectedJob) {
      setJobSearchTerm(`${selectedJob.job_name} - ${selectedJob.bradanna_job_number}`);
    }

    setShowEditModal(true);
  };

  const handleDeleteEntry = (entry: TimeEntryWithDetails) => {
    setEntryToDelete(entry);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!entryToDelete) return;

    try {
      const response = await api.delete(`${API_CONFIG.ENDPOINTS.TIME_ENTRIES}/${entryToDelete.entry_id}`);

      if (response.data.success) {
        setSuccessMessage('Time entry deleted successfully');
        setShowSuccessModal(true);
        setShowDeleteConfirm(false);
        setEntryToDelete(null);
        loadTimeEntries();
      } else {
        setError(response.data.error || 'Failed to delete entry');
        setShowErrorModal(true);
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have permission to delete time entries');
      } else if (err.response?.status === 404) {
        setError('Time entry not found or delete endpoint not available');
      } else {
        setError(err.response?.data?.error || 'Failed to delete entry');
      }
      setShowErrorModal(true);
    }
  };

  const handleAddEntry = (specificDate?: string) => {
    setEditingEntry(null);

    const dateToUse = specificDate || getLocalDateStr(new Date());
    setFormData({
      date: dateToUse,
      clockIn: '07:00',
      clockOut: '16:30',
      jobId: '',
      lunchTaken: false,
      lunchMinutes: 30
    });
    setJobSearchTerm(''); // Clear job search when opening modal

    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingEntry(null);
    setFormData({
      date: '',
      clockIn: '',
      clockOut: '',
      jobId: '',
      lunchTaken: false,
      lunchMinutes: 30
    });
  };

  const handleSaveEntry = async () => {
    if (!selectedEmployee || !formData.date || !formData.clockIn || !formData.jobId) {
      const missingFields = [];
      if (!selectedEmployee) missingFields.push('employee');
      if (!formData.date) missingFields.push('date');
      if (!formData.clockIn) missingFields.push('clockIn');
      if (!formData.jobId) missingFields.push('jobId');

      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      setShowErrorModal(true);
      return;
    }

    try {
      let response;
      if (editingEntry) {
        // For editing existing entries, use the legacy format with ISO strings
        const clockInDateTime = new Date(`${formData.date}T${formData.clockIn}`);
        let clockOutDateTime = null;

        if (formData.clockOut) {
          // Rounded-equal times are a valid 0-hour entry; a genuinely earlier
          // time means next day and must be confirmed, never silently assumed.
          const resolved = resolveClockOut(clockInDateTime.toISOString(), new Date(`${formData.date}T${formData.clockOut}`));
          if (resolved.nextDay && !window.confirm(nextDayConfirmMessage(formData.clockOut, clockInDateTime.toISOString()))) {
            return;
          }
          clockOutDateTime = resolved.date;
        }

        const editPayload = {
          entry_id: editingEntry.entry_id,
          job_id: parseInt(formData.jobId),
          clock_in: clockInDateTime.toISOString(),
          clock_out: clockOutDateTime?.toISOString() || null,
          lunch_taken: formData.lunchTaken,
          lunch_minutes: formData.lunchTaken ? formData.lunchMinutes : 0,
          shop_time_minutes: 0,
          edit_reason: `Edited by ${auth.user?.first_name} ${auth.user?.last_name}`
        };
        response = await api.put(API_CONFIG.ENDPOINTS.EDIT_TIME, editPayload);
      } else {
        // Check for duplicate entries before creating a new one
        const dayStart = new Date(formData.date + 'T00:00:00');
        const dayEnd = new Date(formData.date + 'T23:59:59');

        const checkResponse = await api.get(
          `${API_CONFIG.ENDPOINTS.TIME_ENTRIES}?employee_id=${selectedEmployee.employee_id}&start_date=${dayStart.toISOString()}&end_date=${dayEnd.toISOString()}`
        );

        if (checkResponse.data.success && checkResponse.data.data && checkResponse.data.data.length > 0) {
          const existingEntry = checkResponse.data.data[0];
          const entryDate = new Date(existingEntry.clock_in).toLocaleDateString();
          setError(`This employee already has a time entry for ${entryDate}. Please edit the existing entry instead of creating a duplicate.`);
          setShowErrorModal(true);
          return;
        }

        // For creating new entries, use timezone-aware format
        const timezoneOffset = new Date().getTimezoneOffset();

        let clockOutISO = null;
        if (formData.clockOut) {
          // Parse time as UTC first, then convert to actual UTC using offset
          const clockOutLocalStr = `${formData.date}T${formData.clockOut}:00`;
          const clockInLocalStr = `${formData.date}T${formData.clockIn}:00`;

          const offsetMs = timezoneOffset * 60 * 1000;
          const clockInUTC = new Date(new Date(clockInLocalStr + 'Z').getTime() + offsetMs);
          const clockOutUTC = new Date(new Date(clockOutLocalStr + 'Z').getTime() + offsetMs);

          // Rounded-equal times are a valid 0-hour entry; a genuinely earlier
          // time means next day and must be confirmed, never silently assumed.
          const resolved = resolveClockOut(clockInUTC.toISOString(), clockOutUTC);
          if (resolved.nextDay && !window.confirm(nextDayConfirmMessage(formData.clockOut, clockInUTC.toISOString()))) {
            return;
          }
          clockOutISO = resolved.date.toISOString();
        }

        const payload = {
          employee_id: selectedEmployee.employee_id,
          job_id: parseInt(formData.jobId),
          clock_in_date: formData.date,
          clock_in_time: formData.clockIn,
          timezone_offset_minutes: timezoneOffset,
          clock_out: clockOutISO,
          lunch_taken: formData.lunchTaken,
          lunch_minutes: formData.lunchTaken ? formData.lunchMinutes : 0,
          lat: 0,
          lng: 0
        };
        response = await api.post(API_CONFIG.ENDPOINTS.CREATE_TIME_ENTRY, payload);
      }

      if (response.data.success) {
        setSuccessMessage(editingEntry ? 'Time entry updated successfully' : 'Time entry created successfully');
        setShowSuccessModal(true);
        handleCloseModal();
        loadTimeEntries();
      } else {
        setError(response.data.message || 'Failed to save entry');
        setShowErrorModal(true);
      }
    } catch (err: any) {
      console.error('Failed to save entry:', err);
      setError(err.response?.data?.message || 'Failed to save entry');
      setShowErrorModal(true);
    }
  };

  const handleSelectEmployee = (emp: Employee) => {
    setSelectedEmployee(emp);
    setSearchTerm(`${emp.first_name} ${emp.last_name}`);
    setShowDropdown(false);
  };

  const handleSelectJob = (job: Job) => {
    setFormData({ ...formData, jobId: job.job_id.toString() });
    setJobSearchTerm(`${job.job_name} - ${job.bradanna_job_number}`);
    setShowJobDropdown(false);
  };

  const filteredJobs = jobs.filter(job =>
    job.job_name.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
    job.bradanna_job_number?.toLowerCase().includes(jobSearchTerm.toLowerCase())
  );

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1400px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '32px',
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '16px'
    },
    controls: {
      display: 'grid',
      gap: '20px',
      marginBottom: '24px'
    },
    card: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    dropdownContainer: {
      position: 'relative' as const
    },
    dropdownInput: {
      width: '100%',
      padding: '14px 40px 14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '500',
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: '#1f2937'
    },
    dropdownInputFocused: {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    dropdownIcon: {
      position: 'absolute' as const,
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      color: '#6b7280'
    },
    dropdownList: {
      position: 'absolute' as const,
      top: 'calc(100% + 8px)',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      maxHeight: '400px',
      overflowY: 'auto' as const,
      zIndex: 100,
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
    },
    dropdownItem: {
      padding: '14px 16px',
      cursor: 'pointer',
      borderBottom: '1px solid #f3f4f6',
      transition: 'all 0.15s',
      fontSize: '16px',
      fontWeight: '500',
      color: '#374151'
    },
    dropdownItemHover: {
      backgroundColor: '#f9fafb'
    },
    dropdownItemSelected: {
      backgroundColor: '#dbeafe',
      color: '#1d4ed8',
      fontWeight: '600'
    },
    weekControls: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px'
    },
    weekButton: {
      padding: '12px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      transition: 'all 0.2s',
      color: '#374151'
    },
    weekLabel: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      textAlign: 'center' as const,
      flex: 1
    },
    weekSubLabel: {
      fontSize: '14px',
      color: '#6b7280',
      fontWeight: '400'
    },
    timeSheet: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    dayHeader: {
      backgroundColor: '#f9fafb',
      padding: '16px 24px',
      borderBottom: '2px solid #e5e7eb',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '18px'
    },
    entryRow: {
      padding: '20px 24px',
      borderBottom: '1px solid #f3f4f6',
      transition: 'background-color 0.15s'
    },
    jobName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '12px'
    },
    timeAndActions: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: '12px'
    },
    entryTime: {
      fontFamily: 'monospace',
      color: '#374151',
      fontSize: '20px',
      fontWeight: '600'
    },
    lunchBadge: {
      color: '#f59e0b',
      fontSize: '15px',
      marginTop: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    hoursBadge: {
      fontWeight: '700',
      fontSize: '18px',
      color: '#059669',
      padding: '8px 12px',
      backgroundColor: '#d1fae5',
      borderRadius: '6px'
    },
    entryActions: {
      display: 'flex',
      gap: '8px'
    },
    actionButton: {
      padding: '8px',
      border: '2px solid #e5e7eb',
      borderRadius: '6px',
      cursor: 'pointer',
      backgroundColor: 'white',
      color: '#6b7280',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center'
    },
    totalRow: {
      backgroundColor: '#f9fafb',
      padding: '20px 24px',
      fontWeight: '700',
      fontSize: '20px',
      color: '#1f2937',
      textAlign: 'center' as const,
      borderTop: '2px solid #e5e7eb'
    },
    addButton: {
      margin: '20px 24px',
      padding: '14px 24px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
    },
    loading: {
      textAlign: 'center' as const,
      padding: '60px 20px',
      color: '#6b7280',
      fontSize: '16px'
    },
    error: {
      backgroundColor: '#fef2f2',
      border: '2px solid #fecaca',
      color: '#dc2626',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '16px',
      fontWeight: '500'
    },
    noData: {
      textAlign: 'center' as const,
      padding: '60px 20px',
      color: '#9ca3af',
      fontStyle: 'italic',
      fontSize: '16px'
    },
    modal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflowY: 'auto' as const,
      boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '2px solid #e5e7eb'
    },
    modalTitle: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#1f2937'
    },
    closeButton: {
      padding: '8px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '16px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      transition: 'border-color 0.2s'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'border-color 0.2s'
    },
    checkbox: {
      marginRight: '10px',
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '28px',
      paddingTop: '20px',
      borderTop: '2px solid #e5e7eb'
    },
    saveButton: {
      padding: '12px 24px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
    },
    cancelButton: {
      padding: '12px 24px',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      transition: 'all 0.2s'
    },
    viewToggle: {
      display: 'flex',
      gap: '12px',
      marginTop: '20px'
    },
    toggleButton: {
      padding: '12px 20px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: 'white',
      color: '#6b7280',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s'
    },
    toggleButtonActive: {
      backgroundColor: '#3b82f6',
      color: 'white',
      borderColor: '#3b82f6',
      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
    },
    crewHeader: {
      backgroundColor: '#f9fafb',
      padding: '20px',
      borderRadius: '12px',
      marginBottom: '20px',
      border: '2px solid #e5e7eb'
    },
    crewDay: {
      marginBottom: '32px'
    },
    crewTable: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      overflow: 'hidden'
    },
    crewTableHeader: {
      display: 'flex',
      backgroundColor: '#f9fafb',
      padding: '16px 24px',
      borderBottom: '2px solid #e5e7eb',
      fontSize: '16px',
      fontWeight: '600',
      color: '#374151'
    },
    crewTableRow: {
      display: 'flex',
      padding: '16px 24px',
      borderBottom: '1px solid #f3f4f6',
      fontSize: '16px',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: '8px'
    },
    crewRowHighlight: {
      backgroundColor: '#fef3c7',
      borderLeft: '4px solid #f59e0b'
    },
    addDayButton: {
      padding: '12px 24px',
      border: '2px solid #e5e7eb',
      borderRadius: '6px',
      backgroundColor: 'white',
      color: '#6b7280',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      marginRight: '24px',
      marginBottom: '12px',
      marginTop: '8px',
      float: 'right' as const
    },
    successModal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    },
    successModalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '40px',
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center' as const,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#22c55e',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '48px',
      fontWeight: 'bold',
      margin: '0 auto 24px',
      lineHeight: '80px'
    },
    successTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '12px'
    },
    successMessage: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '24px',
      lineHeight: '1.5'
    },
    successButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 32px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      minWidth: '120px'
    },
    errorModal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    },
    errorModalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '40px',
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center' as const,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    errorIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#ef4444',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '48px',
      fontWeight: 'bold',
      margin: '0 auto 24px',
      lineHeight: '80px'
    },
    errorTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '12px'
    },
    errorMessage: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '24px',
      lineHeight: '1.5'
    },
    errorButton: {
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 32px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      minWidth: '120px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Time Management</h1>
        <p style={styles.subtitle}>Manage employee time entries and schedules</p>

        <div style={styles.viewToggle}>
          <button
            style={{
              ...styles.toggleButton,
              ...(viewMode === 'individual' ? styles.toggleButtonActive : {})
            }}
            onClick={() => setViewMode('individual')}
          >
            Individual View
          </button>
          <button
            style={{
              ...styles.toggleButton,
              ...(viewMode === 'crew' ? styles.toggleButtonActive : {})
            }}
            onClick={() => setViewMode('crew')}
          >
            <Users size={18} />
            Crew View
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {/* Week Selection */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>
          <Calendar size={20} />
          Week Selection
        </h3>

        <div style={styles.weekControls}>
          <button
            style={styles.weekButton}
            onClick={() => setWeekOffset(weekOffset - 1)}
          >
            <ChevronLeft size={20} />
          </button>

          <div style={styles.weekLabel}>
            {weekOffset === 0 ? 'Current Week' : weekOffset === -1 ? 'Previous Week' : `${Math.abs(weekOffset)} weeks ago`}
            <br />
            <span style={styles.weekSubLabel}>
              {formatWeekRange(weekOffset)}
            </span>
          </div>

          <button
            style={styles.weekButton}
            onClick={() => setWeekOffset(weekOffset + 1)}
            disabled={weekOffset >= 0}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Employee Selection Dropdown */}
      <div style={styles.controls}>
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>
            {viewMode === 'individual' ? <Search size={20} /> : <Users size={20} />}
            {viewMode === 'individual' ? 'Select Employee' : 'Select Crew (Foreman)'}
          </h3>

          <div style={styles.dropdownContainer}>
            <input
              type="text"
              placeholder={viewMode === 'individual' ? 'Search employees...' : 'Search foremen...'}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => {
                setSearchTerm('');
                setShowDropdown(true);
              }}
              style={{
                ...styles.dropdownInput,
                ...(showDropdown ? styles.dropdownInputFocused : {})
              }}
            />
            <ChevronDown
              size={20}
              style={styles.dropdownIcon}
              onClick={() => {
                setSearchTerm('');
                setShowDropdown(true);
              }}
            />

            {showDropdown && filteredEmployees.length > 0 && (
              <div style={styles.dropdownList}>
                {filteredEmployees.map(emp => (
                  <div
                    key={emp.employee_id}
                    style={{
                      ...styles.dropdownItem,
                      ...(selectedEmployee?.employee_id === emp.employee_id ? styles.dropdownItemSelected : {})
                    }}
                    onClick={() => handleSelectEmployee(emp)}
                    onMouseEnter={(e) => {
                      if (selectedEmployee?.employee_id !== emp.employee_id) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedEmployee?.employee_id !== emp.employee_id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    {emp.first_name} {emp.last_name}
                    {viewMode === 'crew' && (
                      <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400', marginTop: '2px' }}>
                        Foreman
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time Sheet */}
      {viewMode === 'individual' ? (
        selectedEmployee ? (
          <div style={styles.timeSheet}>
            {loading ? (
              <div style={styles.loading}>
                <Clock size={32} />
                <p>Loading time entries...</p>
              </div>
            ) : weekDays.length === 0 ? (
              <div style={styles.noData}>
                No time entries found for this week
              </div>
            ) : (
              <>
                {weekDays.map((day, index) => {
                  const date = new Date(day.date + 'T12:00:00');
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                  return (
                    <div key={day.date}>
                      <div style={styles.dayHeader}>
                        <span>{dayName}, {dateStr}</span>
                        <span>{day.totalHours.toFixed(2)} hrs</span>
                      </div>

                      {day.entries.length === 0 ? (
                        <div style={{ ...styles.entryRow, color: '#9ca3af', fontStyle: 'italic' }}>
                          No entries for this day
                        </div>
                      ) : (
                        day.entries.map(entry => (
                          <div
                            key={entry.entry_id}
                            style={styles.entryRow}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          >
                            {entry.job_name && (
                              <div style={styles.jobName}>
                                {entry.job_name}
                              </div>
                            )}

                            <div style={styles.timeAndActions}>
                              <div>
                                <div style={styles.entryTime}>
                                  {new Date(entry.clock_in).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                  {entry.clock_out && (
                                    <> - {new Date(entry.clock_out).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}</>
                                  )}
                                </div>

                                {entry.lunch_taken && (
                                  <div style={styles.lunchBadge}>
                                    🍽️ Lunch: {entry.lunch_minutes || 30}min
                                  </div>
                                )}
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <span style={styles.hoursBadge}>
                                  {(entry.hours_worked || 0).toFixed(2)} hrs
                                </span>
                                <div style={styles.entryActions}>
                                  <button
                                    style={styles.actionButton}
                                    onClick={() => handleEditEntry(entry)}
                                    title="Edit entry"
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor = '#3b82f6';
                                      e.currentTarget.style.color = '#3b82f6';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor = '#e5e7eb';
                                      e.currentTarget.style.color = '#6b7280';
                                    }}
                                  >
                                    <Edit size={18} />
                                  </button>
                                  <button
                                    style={styles.actionButton}
                                    onClick={() => handleDeleteEntry(entry)}
                                    title="Delete entry"
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor = '#dc2626';
                                      e.currentTarget.style.color = '#dc2626';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor = '#e5e7eb';
                                      e.currentTarget.style.color = '#6b7280';
                                    }}
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}

                      {/* Add Entry Button for this day */}
                      <button
                        style={styles.addDayButton}
                        onClick={(e) => {
                          e.preventDefault();
                          handleAddEntry(day.date);
                        }}
                        title="Add entry for this day"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.color = '#3b82f6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.color = '#6b7280';
                        }}
                      >
                        <Plus size={18} />
                        Add Entry
                      </button>
                      <div style={{ clear: 'both' }}></div>
                    </div>
                  );
                })}

                <button
                  style={styles.addButton}
                  onClick={() => handleAddEntry()}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Plus size={20} />
                  Add Time Entry
                </button>

                <div style={styles.totalRow}>
                  Total Week Hours: {totalWeekHours.toFixed(2)}
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={styles.noData}>
            Please select an employee to view their time entries
          </div>
        )
      ) : (
        selectedEmployee && selectedEmployee.role === 'foreman' ? (
          <div style={styles.timeSheet}>
            {loading ? (
              <div style={styles.loading}>
                <Clock size={32} />
                <p>Loading crew data...</p>
              </div>
            ) : Object.keys(crewData).length === 0 ? (
              <div style={styles.noData}>
                No crew data found for this week
              </div>
            ) : (
              <>
                <div style={styles.crewHeader}>
                  <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700' }}>
                    {selectedEmployee.first_name} {selectedEmployee.last_name}'s Crew
                  </h2>
                  <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
                    Foreman | {employees.filter(emp => emp.crew_foreman_id === selectedEmployee.employee_id).length + 1} total members
                  </p>
                </div>

                {Object.entries(crewData).map(([dateStr, crewMembers]) => {
                  if (crewMembers.length === 0) return null;

                  const date = new Date(dateStr + 'T12:00:00');
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                  const dateDisplay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                  return (
                    <div key={dateStr} style={styles.crewDay}>
                      <div style={styles.dayHeader}>
                        <span>{dayName}, {dateDisplay}</span>
                        <span>{crewMembers.length} crew member{crewMembers.length > 1 ? 's' : ''}</span>
                      </div>

                      <div style={styles.crewTable}>
                        <div style={styles.crewTableHeader}>
                          <div style={{ flex: 2 }}>Employee</div>
                          <div style={{ flex: 1 }}>Clock In</div>
                          <div style={{ flex: 1 }}>Clock Out</div>
                          <div style={{ flex: 1 }}>Lunch</div>
                          <div style={{ flex: 1 }}>Hours</div>
                        </div>

                        {crewMembers.map(member => {
                          const status = getTimeDifferenceStatus(crewMembers, member);
                          return (
                            <div
                              key={member.employee.employee_id}
                              style={{
                                ...styles.crewTableRow,
                                ...(status === 'highlight' ? styles.crewRowHighlight : {})
                              }}
                            >
                              <div style={{ flex: 2, fontWeight: '600' }}>
                                {member.employee.first_name} {member.employee.last_name}
                              </div>
                              <div style={{ flex: 1 }}>{member.clockInTime}</div>
                              <div style={{ flex: 1 }}>{member.clockOutTime}</div>
                              <div style={{ flex: 1 }}>
                                {member.lunchInfo?.taken ?
                                  `🍽️ ${member.lunchInfo.minutes}min` :
                                  '—'
                                }
                              </div>
                              <div style={{ flex: 1, fontWeight: '700' }}>
                                {member.totalHours.toFixed(2)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        ) : (
          <div style={styles.noData}>
            Please select a foreman to view crew data
          </div>
        )
      )}

      {/* Edit/Add Modal */}
      {showEditModal && (
        <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
              </h2>
              <button
                style={styles.closeButton}
                onClick={handleCloseModal}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1f2937'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                <X size={24} />
              </button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Date *</label>
              <input
                type="date"
                style={styles.input}
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Job *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  style={{
                    ...styles.input,
                    ...(showJobDropdown ? { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' } : {})
                  }}
                  value={jobSearchTerm}
                  onChange={(e) => {
                    setJobSearchTerm(e.target.value);
                    setShowJobDropdown(true);
                  }}
                  onFocus={() => setShowJobDropdown(true)}
                  placeholder="Search jobs..."
                  required
                />
                {showJobDropdown && filteredJobs.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000
                  }}>
                    {filteredJobs.map(job => (
                      <div
                        key={job.job_id}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background-color 0.2s',
                          backgroundColor: formData.jobId === job.job_id.toString() ? '#dbeafe' : 'white'
                        }}
                        onClick={() => handleSelectJob(job)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.jobId === job.job_id.toString() ? '#dbeafe' : 'white'}
                      >
                        {job.job_name} - {job.bradanna_job_number}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Clock In Time *</label>
              <CustomTimePicker
                value={formData.clockIn}
                onChange={(val) => setFormData({ ...formData, clockIn: val })}
                label="Select Clock In Time"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Clock Out Time</label>
              <CustomTimePicker
                value={formData.clockOut}
                onChange={(val) => setFormData({ ...formData, clockOut: val })}
                label="Select Clock Out Time"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={formData.lunchTaken}
                  onChange={(e) => setFormData({ ...formData, lunchTaken: e.target.checked })}
                />
                Lunch Taken
              </label>
            </div>

            {formData.lunchTaken && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Lunch Minutes</label>
                <input
                  type="number"
                  style={styles.input}
                  value={formData.lunchMinutes}
                  onChange={(e) => setFormData({ ...formData, lunchMinutes: parseInt(e.target.value) || 0 })}
                  min="0"
                  max="120"
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            )}

            <div style={styles.modalActions}>
              <button
                style={styles.cancelButton}
                onClick={handleCloseModal}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b7280'}
              >
                Cancel
              </button>
              <button
                style={styles.saveButton}
                onClick={handleSaveEntry}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Save size={18} />
                {editingEntry ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && entryToDelete && (
        <div style={styles.modal}>
          <div style={{
            ...styles.modalContent,
            maxWidth: '500px'
          }}>
            <div style={{
              borderBottom: '4px solid #dc2626',
              padding: '24px',
              backgroundColor: '#fef2f2',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#991b1b',
                textAlign: 'center'
              }}>
                Confirm Delete
              </h2>
            </div>

            <div style={{ padding: '24px' }}>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                Are you sure you want to delete this time entry?
              </p>

              <div style={{
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#1f2937' }}>Employee:</strong>{' '}
                  <span style={{ color: '#374151' }}>
                    {selectedEmployee?.first_name} {selectedEmployee?.last_name}
                  </span>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#1f2937' }}>Date:</strong>{' '}
                  <span style={{ color: '#374151' }}>
                    {new Date(entryToDelete.clock_in).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#1f2937' }}>Time:</strong>{' '}
                  <span style={{ color: '#374151' }}>
                    {new Date(entryToDelete.clock_in).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    {entryToDelete.clock_out && (
                      <> - {new Date(entryToDelete.clock_out).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</>
                    )}
                  </span>
                </div>
                {entryToDelete.clock_out && (
                  <div>
                    <strong style={{ color: '#1f2937' }}>Hours:</strong>{' '}
                    <span style={{ color: '#374151' }}>
                      {(() => {
                        const clockIn = new Date(entryToDelete.clock_in);
                        const clockOut = new Date(entryToDelete.clock_out);
                        let hours = (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60);
                        if (entryToDelete.lunch_taken && entryToDelete.lunch_minutes) {
                          hours -= entryToDelete.lunch_minutes / 60;
                        }
                        return hours.toFixed(2);
                      })()}
                    </span>
                  </div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <button
                  style={{
                    flex: 1,
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: '600',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setEntryToDelete(null);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  Cancel
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: '600',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: '2px solid #dc2626',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={confirmDelete}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#b91c1c';
                    e.currentTarget.style.borderColor = '#b91c1c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                    e.currentTarget.style.borderColor = '#dc2626';
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          style={styles.successModal}
          onClick={() => setShowSuccessModal(false)}
        >
          <div
            style={styles.successModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.successIcon}>✓</div>
            <h3 style={styles.successTitle}>Success!</h3>
            <p style={styles.successMessage}>{successMessage}</p>
            <button
              style={styles.successButton}
              onClick={() => setShowSuccessModal(false)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#22c55e'}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div
          style={styles.errorModal}
          onClick={() => setShowErrorModal(false)}
        >
          <div
            style={styles.errorModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.errorIcon}>!</div>
            <h3 style={styles.errorTitle}>Error</h3>
            <p style={styles.errorMessage}>{error}</p>
            <button
              style={styles.errorButton}
              onClick={() => setShowErrorModal(false)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
